/**
 * functionColor — a function's own colour (fnDef.color), set in the function
 * editor and shown on every Call node for it and its ƒ in My Blueprint.
 *
 * Stored as a #rrggbb string on the function definition. Absent or malformed
 * means the default purple every function had before colours existed, so an
 * older blueprint renders exactly as it did.
 *
 * A PICKED colour is the Call node's title bar exactly as picked. Title text on
 * event-graph nodes is light grey (nodeLook.css), which disappears on a light
 * colour, so the text turns dark when the colour is light (functionTitleInk).
 * An unpicked function keeps the dark purple bar it always had.
 */

export const FUNCTION_DEFAULT_COLOR = '#b080f0'
const DEFAULT_HEADER = '#160736'
const DEFAULT_HEADER_SELECTED = '#200a4a'

const HEX = /^#[0-9a-f]{6}$/i

/** The function's colour, or the default when unset / not a #rrggbb. */
export function functionColor(fnDef) {
  const c = fnDef?.color
  return typeof c === 'string' && HEX.test(c) ? c.toLowerCase() : FUNCTION_DEFAULT_COLOR
}

/** True when the function has a valid colour of its own. */
export function hasFunctionColor(fnDef) {
  return typeof fnDef?.color === 'string' && HEX.test(fnDef.color)
}

/** Title-bar background for a Call node: the exact pick, else the old dark purple. */
export function functionHeaderBg(fnDef, selected = false) {
  if (hasFunctionColor(fnDef)) return functionColor(fnDef)
  return selected ? DEFAULT_HEADER_SELECTED : DEFAULT_HEADER
}

/**
 * 'dark' or 'light' title text for a background, by WCAG relative luminance:
 * whichever of near-black / light-grey text contrasts more with it.
 */
export function functionTitleInk(hex) {
  const ch = i => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  const L = 0.2126 * ch(1) + 0.7152 * ch(3) + 0.0722 * ch(5)
  // Contrast against #111 (L≈0.0056) vs #e6e6e6 (L≈0.79).
  const vsDark = (L + 0.05) / (0.0056 + 0.05)
  const vsLight = (0.79 + 0.05) / (L + 0.05)
  return vsDark > vsLight ? 'dark' : 'light'
}
